import { extend, localize } from 'vee-validate'
import {
  required as rule_required,
  email as rule_email,
  min as rule_min,
  max as rule_max,
  confirmed as rule_confirmed,
  regex as rule_regex,
  between as rule_between,
  alpha as rule_alpha,
  integer as rule_integer,
  digits as rule_digits,
  alpha_dash as rule_alpha_dash,
  alpha_num as rule_alpha_num,
  length as rule_length,
  // max_value as rule_max_value,
} from 'vee-validate/dist/rules'
import i18n from '@/libs/i18n'
import vi from 'vee-validate/dist/locale/vi.json'
import en from 'vee-validate/dist/locale/en.json'

// eslint-disable-next-line object-curly-newline
import {
  validatorTwoDigits,
  validatorChecked,
  validatorPositive,
  validatorUrlValidator,
  validatorPassword,
  validatorCreditCard,
  validatorUrlWithProtocolsValidator,
  validatorGreaterThanZero,
  validatorGreaterThanOne,
  validatorGreaterThanTen,
  validateTopUp,
  validateMinAmount,
  validateTopUpDev,
  validateIntegerNumber,
  validatorFaceBookUrl,
  validatorCodeLength,
  validatorPostalCodeLength,
  validatorTelegramLength,
  validatorExcludeUrl,
  validatorProductLink,
  validPixelScript,
  validatorEqualTwenty,
  validateTip,
  validatorBusinessWebsite,
  validatorPhoneNumberWhatApp,
  validateRefundLocalBank,
  validateAmountNumber,
  validateAutoTopUpAmount,
  validatorVerifyCode, validatorNameLength,
  validatorIdNumber,
} from './validators'

// ////////////////////////////////////////////////////////
// General
// ////////////////////////////////////////////////////////

export const required = extend('required', rule_required)

export const email = extend('email', rule_email)

export const min = extend('min', rule_min)

export const confirmed = extend('confirmed', rule_confirmed)

export const regex = extend('regex', rule_regex)

export const between = extend('between', rule_between)

export const alpha = extend('alpha', rule_alpha)

export const integer = extend('integer', rule_integer)

export const digits = extend('digits', rule_digits)

export const alphaDash = extend('alpha-dash', rule_alpha_dash)

export const alphaNum = extend('alpha-num', rule_alpha_num)

export const length = extend('length', rule_length)

export const max = extend('max', rule_max)

// export const maxValue = extend('maxValue', rule_max_value)

export const positive = extend('positive', {
  validate: validatorPositive,
  message: (_, values) => i18n.t('validationMsg.positive', values),
})

export const greaterThanZero = extend('greaterThanZero', {
  validate: validatorGreaterThanZero,
  message: (_, values) => i18n.t('validationMsg.greaterThanZero', values),
})

export const equalTwenty = extend('equalTwenty', {
  validate: validatorEqualTwenty,
  message: (_, values) => i18n.t('validationMsg.lengthEqual20', values),
})

export const greaterThanOne = extend('greaterThanOne', {
  validate: validatorGreaterThanOne,
  message: (_, values) => i18n.t('validationMsg.amount.greaterThanOrEqual1', values),
})

export const greaterThanTen = extend('greaterThanTen', {
  validate: validatorGreaterThanTen,
  message: (_, values) => i18n.t('validationMsg.amount.greaterThanOrEqual10', values),
})

export const twoDigits = extend('twoDigits', {
  validate: validatorTwoDigits,
  message: (_, values) => i18n.t('validationMsg.amount.max2Decimals', values),
})

export const availableBalance = extend('availableBalance', {
  validate(value, { balance, service, tax }) {
    const total = (value + (value * parseFloat((parseFloat(service) + parseFloat(tax)))))

    return total <= balance
  },
  params: ['balance', 'service', 'tax'],
  message: (_, values) => i18n.t('validationMsg.insufficientFunds', values),
})

export const rangeTopUp = extend('rangeTopUp', {
  params: ['min'],
  validate: validateTopUp,
  // eslint-disable-next-line no-template-curly-in-string
  message: (_, values) => i18n.t('validationMsg.amount.minMax', values),
})

export const minAmount = extend('minAmount', {
  params: ['min'],
  validate: validateMinAmount,
  // eslint-disable-next-line no-template-curly-in-string
  message: (_, values) => i18n.t('validationMsg.amount.minAmount', values),
})

export const rangeAutoTopUpAmount = extend('rangeAutoTopUpAmount', {
  params: ['min', 'max'],
  validate: validateAutoTopUpAmount,
  // eslint-disable-next-line no-template-curly-in-string
  message: (_, values) => i18n.t('validationMsg.amount.invalidAutoTopUpAmount', values),
})

export const rangeTipAmount = extend('rangeTipAmount', {
  params: ['min'],
  validate: validateTip,
  // eslint-disable-next-line no-template-curly-in-string
  message: (_, values) => i18n.t('validationMsg.tipInvalid', values),
})

export const maxTopUpDev = extend('maxTopUpDev', {
  validate: validateTopUpDev,
  message: (_, values) => i18n.t('validationMsg.amountInvalid1', values),
})

export const integerNumber = extend('integerNumber', {
  validate: validateIntegerNumber,
  message: (_, values) => i18n.t('validationMsg.amount.integerNumber', values),
})

export const credit = extend('credit-card', {
  validate: validatorCreditCard,
  message: (_, values) => i18n.t('validationMsg.creditCard', values),
})

export const password = extend('password', {
  validate: validatorPassword,
  message: (_, values) => i18n.t('validationMsg.passwordCheck', values),
})

export const url = extend('url', {
  validate: validatorUrlValidator,
  message: (_, values) => i18n.t('validationMsg.url.invalid', values),
})

export const urlWithProtocols = extend('urlWithProtocols', {
  validate: validatorUrlWithProtocolsValidator,
  message: (_, values) => i18n.t('validationMsg.url.mustStartWithHttpOrHttps', values),
})

export const fbUrl = extend('fbUrl', {
  validate: validatorFaceBookUrl,
  message: (_, values) => i18n.t('validationMsg.url.facebookUrl', values),
})

export const messageLength = extend('messageLength', {
  validate: message => message.trim().length < 51,
  message: (_, values) => i18n.t('validationMsg.messageLength', values),
})

export const verifyCodeLength = extend('verifyCodeLength', {
  validate: validatorCodeLength,
  message: (_, values) => i18n.t('validationMsg.verifyCode', values),
})

export const checked = extend('checked', {
  validate: validatorChecked,
  message: (_, values) => i18n.t('validationMsg.checkRequired', values),
})

export const postalCodeLength = extend('postalCodeLength', {
  validate: validatorPostalCodeLength,
  message: (_, values) => i18n.t('validationMsg.postalCodeLength', values),
})

export const telegramLength = extend('telegramLength', {
  validate: validatorTelegramLength,
  message: (_, values) => i18n.t('validationMsg.telegramLength', values),
})

export const nameLength = extend('nameLength', {
  validate: validatorNameLength,
  message: (_, values) => i18n.t('validationMsg.nameLength', values),
})

export const valExcludeUrl = extend('valExcludeUrl', {
  validate: validatorExcludeUrl,
  message: (_, values) => i18n.t('validationMsg.content.notUrl', values),
})

export const productLink = extend('productLink', {
  validate: validatorProductLink,
  message: (_, values) => i18n.t('validationMsg.url.invalid', values),
})

export const validPixelCode = extend('validPixelCode', {
  validate: validPixelScript,
  message: (_, values) => i18n.t('validationMsg.content.invalidScript', values),
})

export const verifyBusinessWebsite = extend('verifyBusinessWebsite', {
  validate: validatorBusinessWebsite,
  message: (_, values) => i18n.t('validationMsg.content.notOwnBusinessWebsite', values),
})

export const verifyPhoneNumberWhatApp = extend('verifyPhoneNumberWhatApp', {
  validate: validatorPhoneNumberWhatApp,
  message: (_, values) => i18n.t('validationMsg.whatsappNumber', values),
})

export const verifyPhoneNumberVN = extend('verifyPhoneNumberVN', {
  validate: validatorPhoneNumberWhatApp,
  message: (_, values) => i18n.t('validationMsg.phoneNumber', values),
})

export const verifyZaloNumber = extend('verifyZaloNumber', {
  validate: validatorPhoneNumberWhatApp,
  message: (_, values) => i18n.t('validationMsg.zaloNumber', values),
})

export const rangeRefundLocalBank = extend('rangeRefundLocalBank', {
  params: ['min'],
  validate: validateRefundLocalBank,
  // eslint-disable-next-line no-template-curly-in-string
  message: (_, values) => i18n.t('validationMsg.invalidAmount2', values),
})

export const amountNumberVN = extend('amountNumberVN', {
  validate: validateAmountNumber,
  message: (_, values) => i18n.t('validationMsg.integerNumber', values),
})

export const validVerifyCode = extend('validVerifyCode', {
  validate: validatorVerifyCode,
  message: (_, values) => i18n.t('validationMsg.verifyCodeTikTok', values),
})

export const idNumber = extend('idNumber', {
  validate: validatorIdNumber,
  message: (_, values) => i18n.t('validationMsg.idNumber', values),
})
// Install English and Arabic localizations.
localize({
  en: {
    messages: en.messages,
  },
  vi: {
    messages: vi.messages,
  },
})
// ////////////////////////////////////////////////////////
// NOTE:
// Quasar validation for reference only
// Remove this note once development is finished and make sure to
// to convert all of them in veevalidate version
// ////////////////////////////////////////////////////////

// export const required = (val) => {
//   return (val && val.length > 0) || '*Field is required'
// }

// export const required_obj = (obj) => {
//   if (obj === null || obj === undefined) return '*Field is required'
//   return (Object.entries(obj).length > 0 && obj.constructor === Object) || '*Field is required'
// }

// export const no_blank_spaces_arr = (arr) => {
//   return arr.every(val => (val.trim() && val.trim().length > 0)) || 'Blank Spaces are not allowed'
// }

// export const url = val => {
//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // Used
//   // https://stackoverflow.com/questions/4314741/url-regex-validation

//   // Other
//   // https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
//   // https://www.w3resource.com/javascript-exercises/javascript-regexp-exercise-9.php
//   // https://www.geeksforgeeks.org/how-to-validate-url-using-regular-expression-in-javascript/

//   /* eslint-disable no-useless-escape */
//   const re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/
//   /* eslint-enable no-useless-escape */
//   return re.test(val) || 'URL is invalid'
// }

// export const date = val => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // https://github.com/quasarframework/quasar/blob/dev/ui/src/utils/patterns.js
//   return /^-?[\d]+\/[0-1]\d\/[0-3]\d$/.test(val) || 'Date is invalid'
// }

// export const max = (val, max) => {

//   // If blank return
//   if (val === undefined || val === null) return true

//   return val.length <= max || `More than ${max} characters are not allowed`
// }

// export const max_arr = (val, max) => {
//   return val.length <= max || `More than ${max} values are not allowed`
// }

// export const min = (val, min) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   return val.length >= min || `Minimum ${min} characters are required`
// }

// export const num_range = (val, min, max) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   const msg = 'Value is invalid'
//   if (min === null) return val <= max || msg
//   else if (max === null) return val >= min || msg
//   else return (val >= min && val <= max) || msg
// }
